import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { dayKey, formatDay, formatNumber } from '@/components/campaigns/campaignFormat'
import { renderTemplateBody, templateBodyText } from '@/components/campaigns/campaignTemplates'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { CampaignSimulation, SimulatedSend } from '@/services/crmCampaignsService'
import type { WhatsAppTemplate } from '@/services/whatsappService'

interface Row {
  key: string
  day: string
  step: number
  template: string
  sends: number
  sample: SimulatedSend
}

/** Mil destinatarios por cinco pasos son cinco mil filas que nadie lee: se agrupan por día y paso. */
function groupSends(sends: SimulatedSend[]): Row[] {
  const rows = new Map<string, Row>()
  for (const send of sends) {
    const day = dayKey(send.due_at)
    const key = `${day}|${send.paso}`
    const row = rows.get(key)
    if (row) row.sends += 1
    else rows.set(key, { key, day, step: send.paso, template: send.plantilla, sends: 1, sample: send })
  }
  return [...rows.values()].sort((a, b) => a.day.localeCompare(b.day) || a.step - b.step)
}

export function SimulationView({
  simulation,
  templates,
}: {
  simulation: CampaignSimulation
  templates: WhatsAppTemplate[]
}) {
  const { t } = useTranslation()
  const { lang = 'es' } = useParams()
  const rows = useMemo(() => groupSends(simulation.envios), [simulation.envios])

  const skippedByReason = useMemo(() => {
    const counts = new Map<string, number>()
    for (const skip of simulation.omitidos) counts.set(skip.motivo, (counts.get(skip.motivo) ?? 0) + 1)
    return [...counts.entries()]
  }, [simulation.omitidos])

  return (
    <div className="space-y-4">
      <p className="text-sm" data-testid="simulation-summary">
        {t('campaigns.review.simulation_summary', {
          sends: formatNumber(simulation.envios.length, lang),
          skipped: formatNumber(simulation.omitidos.length, lang),
        })}
      </p>

      {rows.length > 0 ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('campaigns.review.col_day')}</TableHead>
                <TableHead className="text-right">{t('campaigns.review.col_step')}</TableHead>
                <TableHead>{t('campaigns.review.col_template')}</TableHead>
                <TableHead className="text-right">{t('campaigns.review.col_sends')}</TableHead>
                <TableHead>{t('campaigns.review.col_sample')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const body = templateBodyText(templates.find((item) => item.name === row.template))
                return (
                  <TableRow key={row.key}>
                    <TableCell className="whitespace-nowrap">{formatDay(row.sample.due_at, lang)}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.step}</TableCell>
                    <TableCell className="whitespace-nowrap font-mono text-xs">{row.template}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatNumber(row.sends, lang)}</TableCell>
                    <TableCell className="min-w-64 whitespace-pre-wrap text-xs text-muted-foreground">
                      {body ? renderTemplateBody(body, row.sample.variables) : '—'}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      ) : null}

      {skippedByReason.length > 0 ? (
        <ul className="space-y-1 text-sm">
          {skippedByReason.map(([reason, count]) => (
            <li key={reason} className="flex items-baseline gap-2">
              <span className="font-medium tabular-nums">{formatNumber(count, lang)}</span>
              <span className="text-muted-foreground">
                {t(`campaigns.skip_reason.${reason}`, { defaultValue: reason })}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
